import SideModal from "components/SideModal";
import { useTranslation } from "contexts/i18nContext";
import { useToast } from "contexts/ToastContext";
import { ChangeEvent, useState } from "react";
import { addAppealEvaluation } from "services/prosecutorDecipilineService";
import { Appeal } from "types";
import { LoadingIcon } from "utils/constant";

interface Props {
  isOpen: boolean;
  setIsOpen: (isOpen: boolean) => void;
  appeal: Appeal | any;
  onRefresh: any;
  addMoreDecision?: boolean;
}

export default function AddEvaluationModal({
  isOpen,
  setIsOpen,
  appeal,
  onRefresh,
}: Props) {
  const { t } = useTranslation();
  const { showToast } = useToast();
  const [loading, setLoading] = useState(false);
  const [formState, setFormState] = useState({
    evaluation_date: "",
    work_speed_evaluation: "",
    work_quality_evaluation: "",
    work_load_evaluation: "",
  });

  const handleChange = (
    e: ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>,
  ) => {
    const { name, value } = e.target;
    setFormState((prev) => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setLoading(true);

    try {
      await addAppealEvaluation({
        ...formState,
        assigned_prosecutor: appeal.user_id_accused_prosecutor_name,
        user_id_assigned_prosecutor: appeal.user_id_assigned_prosecutor,
        appeal_request: appeal.id,
      });

      onRefresh();
      setIsOpen(false);
      showToast(t("prosecutorDecisionRecorded"), "success");
      setFormState({
        work_speed_evaluation: "",
        work_quality_evaluation: "",
        work_load_evaluation: "",
        evaluation_date: "",
      });
    } catch (err: any) {
      console.error("Submission error:", err);
      showToast(t("failedToSubmitProsecutorDecision"), "error");
    } finally {
      setLoading(false);
    }
  };

  if (!isOpen) return null;

  return (
    <SideModal
      isOpen={isOpen}
      setIsOpen={setIsOpen}
      title={t("prosecutorDecisionRegistry")}
    >
      <form onSubmit={handleSubmit} className="space-y-6">
        {/* <div className="text-sm text-gray-500">Formal Recording of Prosecutorial Determination</div> */}

        {/* Decision Details Section */}
        <section className="space-y-4">
          <div className="flex items-center">
            <div className="h-5 w-1 bg-red-700 rounded-r mr-3"></div>
            <h3 className="text-base font-semibold text-gray-800">
              {t("decisionDetails")}
            </h3>
          </div>

          <div className="space-y-4 pl-4">
            <div>
              <label className="block text-xs font-medium text-gray-600 mb-1">
                {t("evaluationDate")}
                <span className="text-red-600">*</span>
              </label>
              <input
                required
                type="date"
                name="evaluation_date"
                value={formState.evaluation_date}
                max={new Date().toISOString().split("T")[0]}
                onChange={handleChange}
                className="w-full border border-gray-300 rounded-md px-3 py-2.5 text-sm bg-white focus:outline-none focus:ring-2 focus:ring-red-500 focus:border-transparent"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Work Speed Evaluation (0-100)
                <span className="text-red-600">*</span>
              </label>
              <input
                required
                min={0}
                max={100}
                step="0.01"
                type="number"
                name="work_speed_evaluation"
                value={formState.work_speed_evaluation}
                onChange={handleChange}
                className="w-full border border-gray-300 rounded-md px-3 py-2.5 text-sm bg-white focus:outline-none focus:ring-2 focus:ring-red-500 focus:border-transparent"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Work Quality Evaluation (0-100)
                <span className="text-red-600">*</span>
              </label>
              <input
                required
                min={0}
                max={100}
                step="0.01"
                type="number"
                name="work_quality_evaluation"
                value={formState.work_quality_evaluation}
                onChange={handleChange}
                className="w-full border border-gray-300 rounded-md px-3 py-2.5 text-sm bg-white focus:outline-none focus:ring-2 focus:ring-red-500 focus:border-transparent"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Work Load Evaluation (0-100)
                <span className="text-red-600">*</span>
              </label>
              <input
                required
                min={0}
                max={100}
                step="0.01"
                type="number"
                name="work_load_evaluation"
                value={formState.work_load_evaluation}
                onChange={handleChange}
                className="w-full border border-gray-300 rounded-md px-3 py-2.5 text-sm bg-white focus:outline-none focus:ring-2 focus:ring-red-500 focus:border-transparent"
              />
            </div>
          </div>
        </section>

        {/* Action Buttons */}
        <div className="pt-4 border-t border-gray-200">
          <div className="flex space-x-3">
            <button
              type="button"
              onClick={() => setIsOpen(false)}
              className="flex-1 px-4 py-2.5 border border-gray-300 text-gray-700 text-sm font-medium rounded-md hover:bg-gray-50 transition-colors"
            >
              {t("cancel")}
            </button>
            <button
              type="submit"
              disabled={
                loading ||
                !formState.work_speed_evaluation ||
                !formState.work_quality_evaluation ||
                !formState.work_load_evaluation
              }
              className={`flex-1 px-4 py-2.5 text-white text-sm font-medium rounded-md transition-colors flex items-center justify-center space-x-2 ${
                loading ||
                !formState.work_speed_evaluation ||
                !formState.work_quality_evaluation ||
                !formState.work_load_evaluation
                  ? "bg-red-400 cursor-not-allowed"
                  : "bg-red-700 hover:bg-red-800"
              }`}
            >
              {loading ? (
                <>
                  {LoadingIcon}
                  <span>{t("processing")}</span>
                </>
              ) : (
                t("submitProsecutorDecision")
              )}
            </button>
          </div>
          <p className="text-xs text-gray-500 text-center mt-3">
            {t("officialProsecutorialDeterminationNote")}
          </p>
        </div>
      </form>
    </SideModal>
  );
}
